import { Image } from '@mntn/select-components'
import { AspectRatio, cn, Pressable, Skeleton } from '@mntn/ui-components'
import { cva } from 'class-variance-authority'
import { Button } from '../shared'

type CardSize = 'sm' | 'md' | 'lg' | 'xl' | 'full'

interface ImageProps {
  src: string
  alt: string
}

const offeringCardVariants = cva('shadow-sm group rounded-none', {
  variants: {
    size: {
      sm: 'w-[120px]',
      md: 'w-[155px]',
      lg: 'w-36',
      xl: 'w-52',
      full: 'w-full',
    },
  },
})

const OFFERING_CARD_ASPECT_RATIO = 4 / 5

interface Props {
  imageProps: ImageProps
  size: CardSize
  className?: string
  handleClick?: () => void
}

export const OfferingCard = ({ imageProps, size, className, handleClick }: Props) => {
  const buttonSize = size === 'sm' || size === 'md' ? 'tiny' : 'default'

  return (
    <Pressable
      className={cn(
        offeringCardVariants({ size }),
        !handleClick && 'cursor-default',
        'relative overflow-hidden',
        className,
      )}
      onPress={handleClick}
    >
      <AspectRatio ratio={OFFERING_CARD_ASPECT_RATIO}>
        <div
          className={cn(
            'relative flex size-full items-center justify-center transition-all duration-300 group',
            handleClick && 'hover:scale-105',
          )}
        >
          <Image
            src={imageProps.src}
            alt={imageProps.alt}
            className="absolute inset-0 size-full object-cover object-top"
          />
          {handleClick && (
            <div className="absolute flex items-center justify-center w-full h-full opacity-0 group-hover:opacity-100 group-hover:bg-dark-bg/20 transition-all duration-200">
              <Button variant="secondary" onClick={handleClick} size={buttonSize}>
                Deal Details
              </Button>
            </div>
          )}
        </div>
      </AspectRatio>
    </Pressable>
  )
}

interface SkeletonProps {
  size: CardSize
}

// biome-ignore lint/plugin: RETIRED, do not copy. Convert to a named function export when touching this.
export const OfferingCardSkeleton: React.FC<SkeletonProps> = ({ size }) => {
  return (
    <div className={cn(offeringCardVariants({ size }))}>
      <AspectRatio ratio={OFFERING_CARD_ASPECT_RATIO}>
        <Skeleton className="bg-sub-surface-bg-inverse w-full h-full" />
      </AspectRatio>
    </div>
  )
}
